import React, { useState, useEffect, useRef, useCallback } from 'react';
import Sidebar from '../../../components/Sidebar';
import MeditationNav from '../MeditationNav';
import { HiMenu, HiSearch, HiPlus, HiInformationCircle } from 'react-icons/hi';
import { MdDelete, MdEdit } from 'react-icons/md';
import Toast from '../../../components/toaster';
import { uploadImage } from '../../../services/assetService';
import EditOrderIcon from '../../../assets/icons/edit-order.svg';
import {
  DndContext,
  closestCenter,
} from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  listMeditationProgramCategories,
  createMeditationProgramCategory,
  updateMeditationProgramCategory,
  getMeditationProgramCategory,
  deleteMeditationProgramCategory,
  reorderMeditationProgramCategories,
} from '../../../services/meditationService';

const HTTP_URL_RE = /^https?:\/\/.+/i;

const normalizeImageUrl = (value) => String(value || '').trim();

/** Validate optional image URL: empty OK; non-empty must be http/https. */
const getImageFieldError = (value) => {
  const trimmed = normalizeImageUrl(value);
  if (!trimmed) return '';
  if (!HTTP_URL_RE.test(trimmed)) return 'Image URL must be a valid HTTP or HTTPS URL';
  return '';
};

/**
 * Attach image to create/update payload.
 * - create: include trimmed URL or null
 * - update: omit when unchanged (preserve); send null/URL when changed or cleared
 */
const applyImageToPayload = (payload, { current, original, isCreate }) => {
  const next = normalizeImageUrl(current);
  if (isCreate) {
    payload.image = next || null;
    return;
  }
  const prev = normalizeImageUrl(original);
  if (next === prev) return;
  payload.image = next || null;
};

/**
 * Optional category image: Main/Sub–style file/URL toggle using shared uploadImage.
 * Mode switches do not clear the current URL. Upload failure keeps the previous value.
 */
const CategoryImageField = ({
  id,
  value,
  error,
  onChange,
  onClear,
  onUploadingChange,
  disabled = false,
  resetKey = 0,
}) => {
  const [imageSource, setImageSource] = useState('url');
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [previewFailed, setPreviewFailed] = useState(false);
  const fileInputRef = useRef(null);

  const trimmed = normalizeImageUrl(value);
  const canPreview = trimmed && HTTP_URL_RE.test(trimmed) && !error;

  useEffect(() => {
    setImageSource('url');
    setUploading(false);
    setUploadError('');
    setPreviewFailed(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, [resetKey]);

  useEffect(() => {
    setPreviewFailed(false);
  }, [trimmed]);

  useEffect(() => {
    onUploadingChange?.(uploading);
  }, [uploading, onUploadingChange]);

  const switchSource = (next) => {
    if (uploading || disabled) return;
    setImageSource(next);
    setUploadError('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file || uploading || disabled) return;

    setUploading(true);
    setUploadError('');
    try {
      const uploadedUrl = await uploadImage(file);
      onChange(uploadedUrl);
    } catch (err) {
      setUploadError(err.message || 'Failed to upload image. Please try again.');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleClear = () => {
    if (uploading || disabled) return;
    setUploadError('');
    if (fileInputRef.current) fileInputRef.current.value = '';
    onClear();
  };

  const showClear = Boolean(trimmed) || Boolean(uploadError);

  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <label htmlFor={id} className="block text-xs font-medium text-gray-700">
          Image <span className="font-normal text-gray-400">(optional)</span>
        </label>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1 group relative">
            <HiInformationCircle className="w-4 h-4 text-gray-400 cursor-pointer" />
            <span
              className={`absolute right-0 top-0 -mt-12 sm:-mt-8 px-2 py-1.5 bg-gray-800 text-white text-xs rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10 sm:whitespace-nowrap leading-tight inline-block ${
                imageSource === 'url'
                  ? 'w-[185px] sm:w-fit sm:max-w-[290px]'
                  : 'w-[175px] sm:w-fit sm:max-w-[240px]'
              }`}
            >
              {imageSource === 'url'
                ? 'Switch left to choose a file from your device'
                : 'Switch right to paste an image URL.'}
            </span>
          </div>
          <button
            type="button"
            aria-label={
              imageSource === 'url' ? 'Switch to file upload' : 'Switch to image URL'
            }
            disabled={uploading || disabled}
            onClick={() => switchSource(imageSource === 'url' ? 'file' : 'url')}
            className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none disabled:opacity-50 ${
              imageSource === 'url' ? 'bg-brand' : 'bg-gray-200'
            }`}
          >
            <span
              className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform shadow-sm ${
                imageSource === 'url' ? 'translate-x-5' : 'translate-x-0.5'
              }`}
            />
          </button>
        </div>
      </div>

      {imageSource === 'url' ? (
        <div>
          <div className="flex gap-2">
            <input
              id={id}
              type="url"
              value={value}
              disabled={uploading || disabled}
              onChange={(e) => {
                setUploadError('');
                onChange(e.target.value);
              }}
              placeholder="https://…"
              className={`flex-1 px-3 py-2 text-sm border rounded-lg focus:outline-none disabled:opacity-50 ${
                error ? 'border-red-300' : 'border-gray-300 focus:border-gray-500'
              }`}
            />
            {showClear && (
              <button
                type="button"
                onClick={handleClear}
                disabled={uploading || disabled}
                className="px-3 py-2 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 whitespace-nowrap disabled:opacity-50"
              >
                Clear
              </button>
            )}
          </div>
          <p className="mt-1 text-xs text-gray-400">
            Optional HTTP/HTTPS URL. Clear removes a saved image.
          </p>
        </div>
      ) : (
        <div>
          <div className="flex gap-2">
            <input
              ref={fileInputRef}
              id={`${id}-file`}
              type="file"
              accept="image/*"
              disabled={uploading || disabled}
              onChange={handleFileSelect}
              className={`flex-1 px-3 py-2 text-sm border rounded-lg focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed ${
                uploadError || error ? 'border-red-300' : 'border-gray-300'
              }`}
            />
            {showClear && (
              <button
                type="button"
                onClick={handleClear}
                disabled={uploading || disabled}
                className="px-3 py-2 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 whitespace-nowrap disabled:opacity-50"
              >
                Clear
              </button>
            )}
          </div>
          {uploading && (
            <div className="mt-2 flex items-center gap-2" role="status" aria-live="polite">
              <svg
                className="animate-spin h-4 w-4 text-brand"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
              <span className="text-xs text-gray-600">Uploading image...</span>
            </div>
          )}
          <p className="mt-1 text-xs text-gray-400">
            Optional image file. Uses the shared Admin upload helper.
          </p>
        </div>
      )}

      {uploadError && <p className="mt-1 text-xs text-red-600">{uploadError}</p>}
      {error && !uploadError && <p className="mt-1 text-xs text-red-600">{error}</p>}

      {canPreview && !previewFailed && !uploading && (
        <div className="mt-2">
          <img
            src={trimmed}
            alt="Category preview"
            className="h-20 w-20 object-cover rounded-lg border border-gray-200 bg-gray-50"
            onError={() => setPreviewFailed(true)}
          />
        </div>
      )}
      {canPreview && previewFailed && !uploading && (
        <p className="mt-2 text-xs text-amber-700">Preview unavailable — URL may be broken.</p>
      )}
    </div>
  );
};

const GridIcon = ({ size = 18 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 18 18"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className="text-gray-400"
  >
    <circle cx="4.5" cy="3" r="1.5" fill="currentColor" />
    <circle cx="4.5" cy="9" r="1.5" fill="currentColor" />
    <circle cx="4.5" cy="15" r="1.5" fill="currentColor" />
    <circle cx="13.5" cy="3" r="1.5" fill="currentColor" />
    <circle cx="13.5" cy="9" r="1.5" fill="currentColor" />
    <circle cx="13.5" cy="15" r="1.5" fill="currentColor" />
  </svg>
);

const SortableReorderItem = ({ id, title, index, image }) => {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };
  const initial = (title || '?').charAt(0);
  const thumb = normalizeImageUrl(image);

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className="flex items-center justify-between p-4 bg-[#FDF6EF] rounded-xl border border-white/10 cursor-grab active:cursor-grabbing touch-none"
    >
      <div className="flex items-center gap-4 flex-1 min-w-0">
        <div className="text-black flex items-center justify-center text-sm font-semibold min-w-[20px]">
          {index + 1}
        </div>
        {thumb ? (
          <img
            src={thumb}
            alt=""
            className="w-10 h-10 rounded-lg object-cover flex-shrink-0"
          />
        ) : (
          <div className="w-10 h-10 rounded-lg bg-brand text-black flex items-center justify-center font-bold flex-shrink-0">
            {initial}
          </div>
        )}
        <p className="text-black font-medium flex-1 min-w-0 break-words line-clamp-2">
          {title}
        </p>
      </div>
      <div className="flex-shrink-0 ml-2">
        <GridIcon size={18} />
      </div>
    </div>
  );
};

const CardThumb = ({ src, title }) => {
  const [failed, setFailed] = useState(false);
  const trimmed = normalizeImageUrl(src);
  if (!trimmed || failed) {
    return (
      <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-brand flex items-center justify-center text-white font-semibold text-sm flex-shrink-0">
        {(title || 'P').charAt(0).toUpperCase()}
      </div>
    );
  }
  return (
    <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0 overflow-hidden">
      <img
        src={trimmed}
        alt={title || ''}
        className="w-full h-full object-cover"
        onError={() => setFailed(true)}
      />
    </div>
  );
};

const sortByOrder = (rows) =>
  [...(rows || [])].sort((a, b) => (a.order || 0) - (b.order || 0));

const Spinner = () => (
  <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
  </svg>
);

const FullPageSpinner = () => (
  <div className="fixed inset-0 ml-0 lg:ml-64 flex items-center justify-center z-40">
    <div className="flex items-center gap-3" role="status" aria-live="polite">
      <svg className="w-12 h-12 animate-spin" viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <linearGradient id="med-program-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#E9D5FF" />
            <stop offset="50%" stopColor="#A855F7" />
            <stop offset="100%" stopColor="#6B21A8" />
          </linearGradient>
        </defs>
        <circle cx="50" cy="50" r="40" stroke="url(#med-program-gradient)" strokeWidth="6" strokeLinecap="round" strokeDasharray="60 200" fill="none" transform="rotate(-90 50 50)" />
      </svg>
    </div>
  </div>
);

const StatusBadge = ({ isActive }) => (
  <span
    className={`px-2 py-1 text-xs font-semibold rounded-lg ${
      isActive ? 'bg-[#9FF7C9]/30 text-[#60BC7C]' : 'bg-[#FFF0F0] text-[#F76464]'
    }`}
  >
    {isActive ? 'Active' : 'Inactive'}
  </span>
);

const emptyForm = () => ({
  title: '',
  description: '',
  image: '',
  is_active: true,
  order: 0,
});

const MeditationProgramCategories = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [items, setItems] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [listMutationLoading, setListMutationLoading] = useState(false);
  const [error, setError] = useState(null);

  const [showForm, setShowForm] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [formData, setFormData] = useState(emptyForm());
  const [formErrors, setFormErrors] = useState({});
  const [formGeneralError, setFormGeneralError] = useState('');
  const [saving, setSaving] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [imageUploading, setImageUploading] = useState(false);
  const [imageResetKey, setImageResetKey] = useState(0);

  const handleImageUploading = useCallback((busy) => {
    setImageUploading(Boolean(busy));
  }, []);

  const [showDeactivateModal, setShowDeactivateModal] = useState(false);
  const [itemToDeactivate, setItemToDeactivate] = useState(null);
  const [deactivating, setDeactivating] = useState(false);
  // When save toggles active → inactive, confirm first then apply this payload
  const [pendingDeactivatePayload, setPendingDeactivatePayload] = useState(null);

  const [showReorderModal, setShowReorderModal] = useState(false);
  const [reorderItems, setReorderItems] = useState([]);
  const [reorderSaving, setReorderSaving] = useState(false);

  const searchTimerRef = useRef(null);
  const listRequestIdRef = useRef(0);
  const isInitialDebounceRef = useRef(true);
  const reorderSavingRef = useRef(false);

  useEffect(() => {
    if (!showReorderModal) {
      document.body.style.overflowX = '';
    }
    return () => {
      document.body.style.overflowX = '';
    };
  }, [showReorderModal]);

  const buildListBody = useCallback((searchText) => {
    const body = {};
    if (searchText) body.searchtext = searchText;
    // Always list all statuses — no is_active filter
    return body;
  }, []);

  const loadList = useCallback(async (searchText = '') => {
    const reqId = ++listRequestIdRef.current;
    setError(null);
    try {
      const res = await listMeditationProgramCategories(buildListBody(searchText));
      if (reqId !== listRequestIdRef.current) return;
      const rows = Array.isArray(res?.data) ? res.data : [];
      setItems(rows);
      setTotalCount(typeof res?.totalCount === 'number' ? res.totalCount : rows.length);
    } catch (err) {
      if (reqId !== listRequestIdRef.current) return;
      setError(err.message || 'Error loading program categories.');
      setItems([]);
      setTotalCount(0);
    } finally {
      if (reqId === listRequestIdRef.current) {
        setIsLoading(false);
        setListMutationLoading(false);
      }
    }
  }, [buildListBody]);

  useEffect(() => {
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    if (isInitialDebounceRef.current) {
      isInitialDebounceRef.current = false;
      return;
    }
    searchTimerRef.current = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 400);
    return () => clearTimeout(searchTimerRef.current);
  }, [searchQuery]);

  useEffect(() => {
    setIsLoading(true);
    loadList(debouncedSearch);
  }, [debouncedSearch, loadList]);

  const refreshList = async () => {
    setListMutationLoading(true);
    await loadList(debouncedSearch);
  };

  const openAdd = () => {
    setFormData(emptyForm());
    setFormErrors({});
    setFormGeneralError('');
    setSelectedItem(null);
    setIsEditing(false);
    setPendingDeactivatePayload(null);
    setImageUploading(false);
    setImageResetKey((k) => k + 1);
    setShowForm(true);
  };

  const openEdit = async (item) => {
    setFormErrors({});
    setFormGeneralError('');
    setIsEditing(true);
    setSelectedItem(item);
    setFormData({
      title: item.title || '',
      description: item.description || '',
      image: item.image || '',
      is_active: item.is_active !== false,
      order: typeof item.order === 'number' ? item.order : 0,
    });
    setPendingDeactivatePayload(null);
    setImageUploading(false);
    setImageResetKey((k) => k + 1);
    setShowForm(true);
    setDetailLoading(true);
    try {
      const res = await getMeditationProgramCategory(item._id);
      const doc = res?.data || res;
      if (doc && doc._id) {
        setSelectedItem(doc);
        setFormData({
          title: doc.title || '',
          description: doc.description || '',
          image: doc.image || '',
          is_active: doc.is_active !== false,
          order: typeof doc.order === 'number' ? doc.order : 0,
        });
      }
    } catch (err) {
      Toast.error(err.message || 'Failed to load program category detail');
    } finally {
      setDetailLoading(false);
    }
  };

  const closeForm = () => {
    if (saving || imageUploading) return;
    setShowForm(false);
    setImageUploading(false);
    setFormData(emptyForm());
    setFormErrors({});
    setFormGeneralError('');
    setSelectedItem(null);
    setIsEditing(false);
    setPendingDeactivatePayload(null);
  };

  const validateForm = () => {
    const errs = {};
    if (!String(formData.title || '').trim()) errs.title = 'Title is required';
    const imageErr = getImageFieldError(formData.image);
    if (imageErr) errs.image = imageErr;
    const orderNum = Number(formData.order);
    if (
      formData.order === '' ||
      formData.order === null ||
      Number.isNaN(orderNum) ||
      !Number.isInteger(orderNum) ||
      orderNum < 0
    ) {
      errs.order = 'Order must be a non-negative integer';
    }
    return errs;
  };

  const handleSave = async () => {
    if (imageUploading) return;
    const errs = validateForm();
    if (Object.keys(errs).length) {
      setFormErrors(errs);
      return;
    }

    const order = Number(formData.order);

    if (isEditing && selectedItem) {
      const wasActive = selectedItem.is_active !== false;
      const willDeactivate = wasActive && formData.is_active === false;
      if (willDeactivate) {
        const deactivatePayload = {
          program_category_id: selectedItem._id,
          title: formData.title.trim(),
          description: formData.description,
          is_active: false,
          order,
        };
        applyImageToPayload(deactivatePayload, {
          current: formData.image,
          original: selectedItem?.image,
          isCreate: false,
        });
        setPendingDeactivatePayload(deactivatePayload);
        setItemToDeactivate(selectedItem);
        setShowDeactivateModal(true);
        return;
      }

      setSaving(true);
      setFormErrors({});
      setFormGeneralError('');
      try {
        const updatePayload = {
          program_category_id: selectedItem._id,
          title: formData.title.trim(),
          description: formData.description,
          is_active: formData.is_active,
          order,
        };
        applyImageToPayload(updatePayload, {
          current: formData.image,
          original: selectedItem?.image,
          isCreate: false,
        });
        await updateMeditationProgramCategory(updatePayload);
        Toast.success('Program category updated successfully');
        closeForm();
        await refreshList();
      } catch (err) {
        setFormGeneralError(err.message || 'Failed to update program category');
      } finally {
        setSaving(false);
      }
      return;
    }

    setSaving(true);
    setFormErrors({});
    setFormGeneralError('');
    try {
      const createPayload = {
        title: formData.title.trim(),
        description: formData.description,
        is_active: formData.is_active,
        order,
      };
      applyImageToPayload(createPayload, {
        current: formData.image,
        original: null,
        isCreate: true,
      });
      await createMeditationProgramCategory(createPayload);
      Toast.success('Program category created successfully');
      closeForm();
      await refreshList();
    } catch (err) {
      setFormGeneralError(err.message || 'Failed to create program category');
    } finally {
      setSaving(false);
    }
  };

  const openSoftDelete = (item) => {
    setPendingDeactivatePayload(null);
    setItemToDeactivate(item);
    setShowDeactivateModal(true);
  };

  const closeDeactivate = () => {
    if (deactivating) return;
    setShowDeactivateModal(false);
    setItemToDeactivate(null);
    setPendingDeactivatePayload(null);
  };

  const confirmDeactivate = async () => {
    if (!itemToDeactivate) return;
    setDeactivating(true);
    try {
      if (pendingDeactivatePayload) {
        await updateMeditationProgramCategory(pendingDeactivatePayload);
      } else {
        await deleteMeditationProgramCategory(itemToDeactivate._id);
      }
      // Soft-delete response is compact; always refresh list from backend
      setShowDeactivateModal(false);
      setItemToDeactivate(null);
      setPendingDeactivatePayload(null);
      setShowForm(false);
      setFormData(emptyForm());
      setSelectedItem(null);
      setIsEditing(false);
      await refreshList();
      Toast.success('Program category deactivated successfully');
    } catch (err) {
      Toast.error(err.message || 'Failed to deactivate program category');
    } finally {
      setDeactivating(false);
    }
  };

  // Search filters the list — reorder requires the full unfiltered set
  const searchActive = Boolean(String(searchQuery || '').trim() || debouncedSearch);

  const openReorder = () => {
    if (reorderSaving || !items.length || searchActive) return;
    setReorderItems(sortByOrder(items));
    setShowReorderModal(true);
  };

  const closeReorder = () => {
    if (reorderSaving) return;
    setShowReorderModal(false);
    setReorderItems([]);
  };

  const handleSaveOrder = async () => {
    if (reorderSavingRef.current || !reorderItems.length) return;
    reorderSavingRef.current = true;
    setReorderSaving(true);
    try {
      await reorderMeditationProgramCategories({
        program_categories: reorderItems.map((item, index) => ({
          program_category_id: item._id,
          order: index + 1,
        })),
      });
      setShowReorderModal(false);
      setReorderItems([]);
      await refreshList();
      Toast.success('Program category order updated successfully');
    } catch (err) {
      Toast.error(err.message || 'Failed to save program category order');
      setShowReorderModal(false);
      setReorderItems([]);
      await refreshList();
    } finally {
      reorderSavingRef.current = false;
      setReorderSaving(false);
    }
  };

  const showLoading = isLoading || listMutationLoading;
  const hasFilters = Boolean(debouncedSearch);

  return (
    <div className="h-screen flex overflow-hidden">
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      <div className="flex-1 bg-primary flex flex-col px-4 py-4 md:p-6 overflow-hidden">
        <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
          <div className="mb-4 md:mb-6">
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => setSidebarOpen(true)}
                className="lg:hidden p-2 rounded-lg hover:bg-white/50 transition-colors"
                aria-label="Open menu"
              >
                <HiMenu className="w-6 h-6 text-gray-800" />
              </button>
              <div>
                <h1 className="text-xl md:text-3xl font-bold text-gray-800">Meditation</h1>
                <p className="text-sm text-gray-500 mt-0.5">Program Categories</p>
              </div>
            </div>
          </div>

          <MeditationNav />

          <div className="mb-4 md:mb-6 flex-shrink-0">
            <div className="flex flex-col md:flex-row md:justify-between md:items-end gap-3 md:gap-4">
              <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto md:flex-1 min-w-0">
                <div className="w-full sm:w-64 relative flex-shrink-0">
                  <HiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-450 w-4 h-4" />
                  <input
                    type="text"
                    placeholder="Search title or description..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-9 pr-3 py-2 md:py-1.5 text-sm border border-[#ECECEC] rounded-xl focus:outline-none bg-white"
                    aria-label="Search program categories"
                  />
                </div>
              </div>
              <div className="flex flex-col items-start md:items-end gap-1 flex-shrink-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <button
                    type="button"
                    onClick={openReorder}
                    disabled={!items.length || reorderSaving || searchActive}
                    title={
                      searchActive
                        ? 'Clear search to reorder all program categories'
                        : 'Reorder all program categories'
                    }
                    className="inline-flex items-center gap-2 px-3 py-2 text-sm font-semibold text-white rounded-xl shadow-md hover:shadow-lg transition-all bg-brand hover:opacity-90 whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                    aria-label={
                      searchActive
                        ? 'Reorder unavailable while search is active'
                        : 'Reorder program categories'
                    }
                  >
                    <img
                      src={EditOrderIcon}
                      alt=""
                      className="w-5 h-5"
                      style={{ filter: 'brightness(0) invert(1)' }}
                    />
                    Reorder
                  </button>
                  {(totalCount > 0 || hasFilters || Boolean(error)) && (
                    <button
                      type="button"
                      onClick={openAdd}
                      className="inline-flex items-center gap-2 px-3 py-2 text-sm font-semibold text-white rounded-xl shadow-md hover:shadow-lg transition-all bg-brand hover:opacity-90 whitespace-nowrap"
                    >
                      <HiPlus className="w-5 h-5" />
                      Add Program Category
                    </button>
                  )}
                </div>
                {searchActive && (
                  <p className="text-xs text-amber-700">
                    Clear search to reorder the full program category list.
                  </p>
                )}
              </div>
            </div>
          </div>

          {showLoading ? (
            <FullPageSpinner />
          ) : totalCount === 0 && !error ? (
            <div className="flex-1 min-h-0 flex items-center justify-center px-4">
              <div className="text-center text-gray-500 max-w-md">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">
                  {hasFilters ? 'No matching program categories' : 'No program categories yet'}
                </h3>
                <p className="text-sm text-gray-500 mb-6">
                  {hasFilters
                    ? 'Try adjusting search or status filters'
                    : 'Create a program category to organize meditation sessions'}
                </p>
                {!hasFilters && (
                  <button
                    type="button"
                    onClick={openAdd}
                    className="inline-flex items-center gap-2 px-6 py-2.5 text-sm font-semibold text-white rounded-xl shadow-md bg-brand hover:opacity-90"
                  >
                    <HiPlus className="w-4 h-4" /> Add Program Category
                  </button>
                )}
              </div>
            </div>
          ) : error ? (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center px-4">
                <p className="text-red-500 mb-3">{error}</p>
                <button
                  type="button"
                  onClick={() => {
                    setIsLoading(true);
                    loadList(debouncedSearch);
                  }}
                  className="px-4 py-2 text-sm font-medium text-white bg-brand rounded-lg hover:opacity-90"
                >
                  Retry
                </button>
              </div>
            </div>
          ) : (
            <div className="flex-1 min-h-0 overflow-y-auto pb-4">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 3xl:grid-cols-4 gap-4">
                {items.map((item) => {
                  const isActive = item.is_active !== false;
                  return (
                    <div
                      key={item._id}
                      className="bg-white rounded-xl p-4 sm:p-5 transition-shadow relative overflow-hidden"
                      style={{ boxShadow: '0px 3px 8px 0px #93939340' }}
                    >
                      <div className="flex items-center gap-3 mb-3 min-w-0">
                        <CardThumb src={item.image} title={item.title} />
                        <div className="h-10 w-px bg-[#F2E2F7] flex-shrink-0" />
                        <h3 className="text-sm sm:text-base font-bold text-gray-900 truncate flex-1">
                          {item.title}
                        </h3>
                      </div>

                      <div className="mb-10">
                        <p className="text-xs sm:text-sm text-gray-500 line-clamp-2 overflow-hidden">
                          {item.description || 'No description available'}
                        </p>
                      </div>

                      <div className="absolute bottom-3 left-3">
                        <StatusBadge isActive={isActive} />
                      </div>

                      <div className="absolute bottom-3 right-3 flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => openEdit(item)}
                          className="w-8 h-8 rounded-lg transition-colors hover:opacity-90 text-brand bg-[#EFEBF3] flex items-center justify-center"
                          title="Edit"
                          aria-label="Edit program category"
                        >
                          <MdEdit className="w-4 h-4" style={{ color: '#58327E' }} />
                        </button>
                        <button
                          type="button"
                          onClick={() => openSoftDelete(item)}
                          disabled={!isActive}
                          className="w-8 h-8 rounded-lg transition-colors hover:opacity-90 text-[#F76464] bg-[#FFF0F0] flex items-center justify-center disabled:opacity-40 disabled:cursor-not-allowed"
                          title={
                            !isActive ? 'Already inactive' : 'Deactivate program category'
                          }
                          aria-label="Deactivate program category"
                        >
                          <MdDelete className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div
            className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] flex flex-col"
            role="dialog"
            aria-modal="true"
            aria-labelledby="program-form-title"
          >
            <div className="px-6 py-4 border-b border-gray-200 flex-shrink-0">
              <h2 id="program-form-title" className="text-lg font-bold text-gray-800">
                {isEditing ? 'Edit Program Category' : 'Add Program Category'}
              </h2>
            </div>
            <div className="px-6 py-4 space-y-4 overflow-y-auto flex-1">
              {detailLoading && (
                <p className="text-sm text-gray-500" role="status">
                  Loading latest details…
                </p>
              )}
              {formGeneralError && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm whitespace-pre-line">
                  {formGeneralError}
                </div>
              )}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Title <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => {
                    setFormData({ ...formData, title: e.target.value });
                    if (formErrors.title) setFormErrors({ ...formErrors, title: '' });
                  }}
                  className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-none ${
                    formErrors.title ? 'border-red-300' : 'border-gray-300 focus:border-gray-500'
                  }`}
                />
                {formErrors.title && (
                  <p className="mt-1 text-xs text-red-600">{formErrors.title}</p>
                )}
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none resize-none focus:border-gray-500"
                />
              </div>
              <CategoryImageField
                id="program-category-image"
                resetKey={imageResetKey}
                value={formData.image}
                error={formErrors.image}
                disabled={saving || detailLoading}
                onUploadingChange={handleImageUploading}
                onChange={(next) => {
                  setFormData((prev) => ({ ...prev, image: next }));
                  if (formErrors.image) setFormErrors((prev) => ({ ...prev, image: '' }));
                }}
                onClear={() => {
                  setFormData((prev) => ({ ...prev, image: '' }));
                  if (formErrors.image) setFormErrors((prev) => ({ ...prev, image: '' }));
                }}
              />
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Order <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  min={0}
                  step={1}
                  value={formData.order}
                  onChange={(e) => {
                    setFormData({ ...formData, order: e.target.value });
                    if (formErrors.order) setFormErrors({ ...formErrors, order: '' });
                  }}
                  className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-none ${
                    formErrors.order ? 'border-red-300' : 'border-gray-300 focus:border-gray-500'
                  }`}
                />
                {formErrors.order && (
                  <p className="mt-1 text-xs text-red-600">{formErrors.order}</p>
                )}
              </div>
              {isEditing &&
                selectedItem?.is_active !== false &&
                formData.is_active === false && (
                  <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2">
                    Deactivating does not remove this category from existing session references.
                    You will be asked to confirm before saving.
                  </p>
                )}
            </div>
            <div className="px-6 py-4 flex items-center justify-between gap-3 border-t border-gray-200 flex-shrink-0">
              <div className="flex items-center gap-3">
                <span className="text-xs font-medium text-gray-700">Active</span>
                <button
                  type="button"
                  role="switch"
                  aria-checked={formData.is_active}
                  onClick={() => setFormData({ ...formData, is_active: !formData.is_active })}
                  disabled={saving || detailLoading || imageUploading}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors disabled:opacity-50 ${
                    formData.is_active ? 'bg-brand' : 'bg-gray-300'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      formData.is_active ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={closeForm}
                  disabled={saving || imageUploading}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={saving || detailLoading || imageUploading}
                  className="px-4 py-2 text-sm font-medium text-white bg-brand rounded-lg hover:opacity-90 disabled:opacity-50 flex items-center gap-2"
                >
                  {saving ? (
                    <>
                      <Spinner />
                      {isEditing ? 'Updating...' : 'Saving...'}
                    </>
                  ) : imageUploading ? (
                    'Uploading...'
                  ) : isEditing ? (
                    'Update'
                  ) : (
                    'Save'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showReorderModal && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4 overflow-hidden">
          <div
            className="bg-[#ffffff] w-full max-w-lg rounded-2xl md:ml-0 lg:ml-8 xl:ml-0 p-6 flex flex-col max-h-[90vh] overflow-hidden"
            role="dialog"
            aria-modal="true"
            aria-labelledby="program-reorder-title"
          >
            <div className="flex justify-between items-center mb-4 flex-shrink-0">
              <h2 id="program-reorder-title" className="text-black/60 text-lg font-semibold">
                Reorder Program Categories
              </h2>
            </div>
            <p className="text-sm text-gray-400 mb-4 flex-shrink-0">
              Drag and drop program categories to reorder them. Click <b>Save Order</b> to apply.
              This updates the full list (search must be cleared first). Does not change Sessions.
            </p>
            <DndContext
              collisionDetection={closestCenter}
              onDragStart={() => {
                document.body.style.overflowX = 'hidden';
              }}
              onDragEnd={(event) => {
                document.body.style.overflowX = '';
                const { active, over } = event;
                if (!over || active.id === over.id) return;
                setReorderItems((prev) => {
                  const oldIndex = prev.findIndex((m) => m._id === active.id);
                  const newIndex = prev.findIndex((m) => m._id === over.id);
                  if (oldIndex < 0 || newIndex < 0) return prev;
                  return arrayMove(prev, oldIndex, newIndex);
                });
              }}
              onDragCancel={() => {
                document.body.style.overflowX = '';
              }}
            >
              <SortableContext
                items={reorderItems.map((m) => m._id)}
                strategy={verticalListSortingStrategy}
              >
                <div
                  className="space-y-3 overflow-y-auto overflow-x-hidden pr-2 flex-1 min-h-0"
                  style={{ maxHeight: '420px' }}
                >
                  {reorderItems.map((item, index) => (
                    <SortableReorderItem
                      key={item._id}
                      id={item._id}
                      title={item.title}
                      image={item.image}
                      index={index}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
            <div className="flex justify-end gap-3 mt-6 flex-shrink-0">
              <button
                type="button"
                onClick={closeReorder}
                disabled={reorderSaving}
                className="px-4 py-2 rounded-lg border border-gray-600 text-gray-500 hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSaveOrder}
                disabled={reorderSaving || !reorderItems.length}
                className="px-4 py-2 rounded-lg bg-brand text-white hover:opacity-90 disabled:opacity-50 flex items-center gap-2"
              >
                {reorderSaving ? (
                  <>
                    <Spinner />
                    Saving...
                  </>
                ) : (
                  'Save Order'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {showDeactivateModal && itemToDeactivate && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-[60] flex items-center justify-center p-6">
          <div
            className="bg-white rounded-lg shadow-xl max-w-md w-full"
            role="dialog"
            aria-modal="true"
            aria-labelledby="program-deactivate-title"
          >
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 id="program-deactivate-title" className="text-xl font-bold text-gray-800">
                Deactivate Program Category
              </h2>
            </div>
            <div className="px-6 py-4 space-y-2">
              <p className="text-gray-700">
                Deactivate <strong>{itemToDeactivate.title}</strong>?
              </p>
              <p className="text-sm text-gray-600">
                This soft-deactivates the category only. Session references are not removed.
              </p>
            </div>
            <div className="px-6 py-4 flex justify-end gap-3">
              <button
                type="button"
                onClick={closeDeactivate}
                disabled={deactivating}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmDeactivate}
                disabled={deactivating}
                className="px-4 py-2 text-sm font-medium text-white bg-[#F76464] rounded-lg disabled:opacity-50 flex items-center gap-2"
              >
                {deactivating ? (
                  <>
                    <Spinner />
                    Deactivating...
                  </>
                ) : (
                  'Confirm Deactivate'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MeditationProgramCategories;
